import {
  Container,
  Box,
  Button,
  Text,
  Link,
  Image,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { Head } from '@/components/Elements/Head';
import { EthToMantle } from '@/components/Icons/chains/EthereumToMantle';
import { AliceLogo } from '@/components/Icons/slash/AliceLogo';
import { CoinbaseLogoIcon, MetamaskFoxIcon, WalletConnectIcon } from '@/components/Icons/wallets';
import { Wrapper } from '@/components/Layouts';
import { Urls } from '@/constants/urls';
import { useEthereumInjected } from '@/features/common/hooks/useEthereumInjected';

const SignIn = () => {
  const { isConnected } = useAccount();
  const { connectors, connectAsync, isPending } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const router = useRouter();
  const { t } = useTranslation(['pages/signin', 'common']);
  const isInjectedEthereum = useEthereumInjected();

  const onConnect = async (connectorId: string) => {
    try {
      if (isConnected) {
        await disconnectAsync();
      }

      const connector = connectors.find((connectorItem) => connectorItem.id === connectorId);

      if (!connector) {
        return;
      }

      await connectAsync({
        connector,
      });
      router.push('/');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <Head />
      <Wrapper
        py={{
          base: 16,
        }}
        mb={28}
        width="100%"
        maxW={{
          base: '90%',
        }}
        margin={{
          base: 'auto',
        }}
      >
        <Box margin="0 auto" mb={{ base: 4, md: 16 }} w={{ base: '195px', md: '300px' }}>
          <AliceLogo />
        </Box>
        <Text
          as="h2"
          textAlign="center"
          color="brand.staticWhite"
          fontSize={{
            base: '24px',
            md: '40px',
          }}
          fontWeight="bold"
          letterSpacing="0.05em"
          mb={{ base: 6, md: 10 }}
        >
          Mantle Bridge Station
        </Text>
        <Text
          color="brand.staticWhite"
          fontSize={{ base: '12px', lg: '17px' }}
          fontWeight="600"
          maxW="530px"
          margin={{ base: '0 auto 24px', md: '0 auto 40px' }}
          letterSpacing={{ base: '0', lg: '0.05em' }}
          textAlign="center"
          whiteSpace="pre-wrap"
        >
          {t('common:common.components.layouts.fixedContent.description')}
        </Text>
        <Text
          color="brand.staticWhite"
          fontSize={{ base: '13px', md: '17px' }}
          fontWeight="600"
          margin={{ base: '0 auto', md: '0 auto 40px' }}
          letterSpacing="0.05em"
        >
          {t('common:common.components.layouts.fixedContent.contact')}
          <Link color="brand.staticWhite" textDecoration="underline" href={Urls.DISCORD_URL} target="_blank" rel="noopener noreferrer">
            {t('common:common.components.layouts.fixedContent.contactLink')}
          </Link>
        </Text>
        <Box
          margin={{ base: '0 auto 32px', md: '0 auto 56px' }}
          w={{
            base: '90%',
            lg: '658px',
          }}
        >
          <EthToMantle />
        </Box>
        <Container
          width="100%"
          height="100%"
          maxWidth="460px"
          maxHeight="640px"
          bg="brand.boxWhite"
          px={{ base: '0.8rem', md: '1.5rem' }}
          pt={{ base: 8, md: 10 }}
          pb={{ base: 10, md: 12 }}
          borderRadius="20px"
          border="1px solid white"
          position="relative"
        >
          <Image
            position="absolute"
            right={{
              base: '-16px',
              sm: '0',
              lg: '-100px',
            }}
            top={{
              base: '-30px',
              lg: '-54px',
            }}
            width={{
              base: '91px',
              sm: '120px',
              lg: '140px',
            }}
            src="/images/pages/hover-item.png"
            alt=""
            animation="hover"
          />
          <Text
            as="h2"
            mb={{ base: 6, md: 10 }}
            letterSpacing="0.05em"
            lineHeight="1.13"
            fontWeight="bold"
            textAlign="center"
            fontSize={{
              base: '24px',
              md: '40px',
            }}
            color="brand.staticWhite"
          >
            Login
          </Text>
          <Box maxW="477px" w="100%" margin="auto">
            <Button
              display={isInjectedEthereum ? 'flex' : 'none'}
              onClick={() => onConnect('injected')}
              h={{
                base: '54px',
                md: '58px',
              }}
              minH="auto"
              maxH={32}
              loading={isPending}
              disabled={isPending}
              borderRadius="1rem"
              gap={2}
              w="100%"
              color="brand.staticWhite"
              fontSize="16px"
              fontWeight="bold"
              letterSpacing="1px"
              style={{
                background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
                border: 'none',
              }}
              _hover={{
                opacity: 0.8,
              }}
            >
              <MetamaskFoxIcon boxSize={7} />
              <Text as="span" fontSize={{ base: '17px', md: '18px' }}>
                {t('common:common.components.elements.buttons.metaMask')}
              </Text>
            </Button>
            <Button
              onClick={() => onConnect('coinbaseWalletSDK')}
              mt={{ base: '12px', md: '12px' }}
              h={{
                base: '54px',
                md: '58px',
              }}
              minH="auto"
              maxH={32}
              loading={isPending}
              disabled={isPending}
              borderRadius="1rem"
              gap={2}
              w="100%"
              color="brand.staticWhite"
              fontSize="16px"
              fontWeight="bold"
              letterSpacing="1px"
              style={{
                background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
                border: 'none',
              }}
              _hover={{
                opacity: 0.8,
              }}
            >
              <CoinbaseLogoIcon boxSize={7} />
              <Text as="span" fontSize={{ base: '17px', md: '18px' }}>
                {t('common:common.components.elements.buttons.coinbase')}
              </Text>
            </Button>
            <Button
              onClick={() => onConnect('walletConnect')}
              mt={{ base: '12px', md: '12px' }}
              h={{
                base: '54px',
                md: '58px',
              }}
              minH="auto"
              maxH={32}
              loading={isPending}
              disabled={isPending}
              borderRadius="1rem"
              gap={2}
              w="100%"
              color="brand.staticWhite"
              fontSize="16px"
              fontWeight="bold"
              letterSpacing="1px"
              style={{
                background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
                border: 'none',
              }}
              _hover={{
                opacity: 0.8,
              }}
            >
              <WalletConnectIcon boxSize={7} />
              <Text as="span" fontSize={{ base: '15px', md: '18px' }}>
                {t('common:common.components.elements.buttons.walletConnect')}
              </Text>
            </Button>
          </Box>
        </Container>
      </Wrapper>
    </>
  );
};

export default SignIn;
